import { Link } from "@tanstack/react-router";
import { useEffect, useState, type ComponentType } from "react";
import { Menu, X } from "lucide-react";
import { PRIMARY_NAV, PRIMARY_NAV_GROUPS, SITE } from "@/lib/routes";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";

// The desktop dropdown groups pull in radix's dropdown-menu + floating-ui.
// They load after the page has hydrated, so that machinery is not in the
// initial per-page bundle; the plain hub-link fallback renders until then.
// No React.lazy/Suspense: a component that suspends during hydration makes
// React hold back the rest of the page for its ~300 ms reveal throttle, and
// the Mediavine script inserts ads in that window (React error #418).
function useHeaderNavGroups() {
  const [Groups, setGroups] = useState<ComponentType | null>(null);
  useEffect(() => {
    let live = true;
    import("./HeaderNavGroups").then((m) => {
      if (live) setGroups(() => m.default);
    });
    return () => {
      live = false;
    };
  }, []);
  return Groups;
}

// SSR/loading fallback for the desktop nav groups: plain links to each group
// hub. Same footprint as the real triggers (no layout shift), fully crawlable,
// and functional before the dropdown chunk hydrates.
function HeaderNavGroupsFallback() {
  return (
    <>
      {PRIMARY_NAV_GROUPS.map((group) => (
        <Link
          key={group.label}
          to={group.hubPath ?? "/"}
          className="rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {group.label}
        </Link>
      ))}
    </>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const HeaderNavGroups = useHeaderNavGroups();

  // Close mobile menu on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/60 bg-background/80 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
        <Link
          to="/"
          className="flex items-center rounded-md text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label={`${SITE.name}.com home`}
        >
          <Logo className="h-8 w-auto" />
        </Link>
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {PRIMARY_NAV.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              activeOptions={{ exact: false }}
              activeProps={{ className: "bg-accent text-foreground", "aria-current": "page" }}
              className="rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {item.label}
            </Link>
          ))}
          {HeaderNavGroups ? <HeaderNavGroups /> : <HeaderNavGroupsFallback />}
        </nav>
        <div className="hidden md:block">
          <Button asChild size="sm">
            <Link to="/barcode-reader">Open Reader</Link>
          </Button>
        </div>
        <button
          type="button"
          className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-border text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="primary-mobile-nav"
        >
          {open ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
        </button>
      </div>
      {open && (
        <div
          id="primary-mobile-nav"
          className="max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain border-t border-border bg-background lg:hidden"
        >
          <nav className="mx-auto flex max-w-6xl flex-col px-4 py-2" aria-label="Mobile">
            {PRIMARY_NAV.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                activeProps={{ className: "bg-accent", "aria-current": "page" }}
                className="rounded-md px-3 py-3 text-base text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            {PRIMARY_NAV_GROUPS.map((group) =>
              group.items.length === 0 && group.hubPath ? (
                <div key={group.label} className="mt-2 flex flex-col border-t border-border pt-2">
                  <Link
                    to={group.hubPath}
                    activeProps={{ className: "bg-accent", "aria-current": "page" }}
                    className="rounded-md px-3 py-3 text-base text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => setOpen(false)}
                  >
                    {group.label}
                  </Link>
                </div>
              ) : (
              <div key={group.label} className="mt-2 flex flex-col border-t border-border pt-2">
                {group.hubPath ? (
                  <Link
                    to={group.hubPath}
                    activeProps={{ className: "bg-accent", "aria-current": "page" }}
                    className="rounded-md px-3 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:bg-accent hover:text-foreground"
                    onClick={() => setOpen(false)}
                  >
                    {group.label} — overview
                  </Link>
                ) : (
                  <div className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {group.label}
                  </div>
                )}
                {group.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    activeProps={{ className: "bg-accent", "aria-current": "page" }}
                    className="rounded-md px-3 py-3 text-base text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => setOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            ))}
            <Link
              to="/barcode-reader"
              className="mt-2 rounded-md bg-primary px-3 py-3 text-center text-base font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => setOpen(false)}
            >
              Open Reader
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
